import React, { useState, useEffect } from 'react';
import { useConnectionMode } from '@/connection/SimpleConnectionProvider';
import { getNetworkInfo, getNetworkDescription, shouldAutoEnableLiteMode } from '@/connection/networkUtils';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Wifi, WifiOff, Info } from 'lucide-react';

export const NetworkDebugInfo: React.FC = () => {
  const [isExpanded, setIsExpanded] = useState(false);
  const [networkInfo, setNetworkInfo] = useState(getNetworkInfo());
  const currentMode = useConnectionMode();
  
  useEffect(() => {
    const updateNetworkInfo = () => {
      setNetworkInfo(getNetworkInfo());
    };
    
    // Update network info when connection changes
    window.addEventListener('online', updateNetworkInfo);
    window.addEventListener('offline', updateNetworkInfo);
    
    const conn = (navigator as any).connection;
    if (conn && typeof conn.addEventListener === 'function') {
      conn.addEventListener('change', updateNetworkInfo);
    }
    
    return () => {
      window.removeEventListener('online', updateNetworkInfo);
      window.removeEventListener('offline', updateNetworkInfo);
      conn?.removeEventListener?.('change', updateNetworkInfo);
    };
  }, []);
  
  const autoLiteEnabled = shouldAutoEnableLiteMode();
  const storedMode = localStorage.getItem('mode');
  const isUserOverride = !!storedMode;
  
  return (
    <div className="fixed bottom-4 right-4 z-50">
      <Button
        variant="outline"
        size="sm"
        onClick={() => setIsExpanded(!isExpanded)}
        className="mb-2 bg-white/90 backdrop-blur-sm"
      >
        {networkInfo.online ? <Wifi className="h-4 w-4" /> : <WifiOff className="h-4 w-4" />}
        <span className="ml-1 text-xs">{currentMode.toUpperCase()}</span>
        <Info className="h-3 w-3 ml-1" />
      </Button>
      
      {isExpanded && (
        <Card className="w-80 bg-white/95 backdrop-blur-sm shadow-lg">
          <CardHeader className="pb-3">
            <CardTitle className="text-sm flex items-center gap-2">
              Network & Mode Info
              <Badge variant={currentMode === 'lite' ? 'secondary' : 'default'} className="text-xs">
                {currentMode}
              </Badge>
            </CardTitle>
          </CardHeader>
          
          <CardContent className="pt-0 space-y-3 text-xs">
            <div>
              <div className="font-medium text-gray-700">Network Status:</div>
              <div className="text-gray-600">{getNetworkDescription()}</div>
            </div>
            
            <div>
              <div className="font-medium text-gray-700">Connection Details:</div>
              <div className="text-gray-600 space-y-1">
                <div>Type: {networkInfo.effectiveType || 'Unknown'}</div>
                {networkInfo.downlink && (
                  <div>Speed: {networkInfo.downlink.toFixed(1)} Mbps</div>
                )}
                <div>Data Saver: {networkInfo.saveData ? 'ON' : 'OFF'}</div>
                <div>Online: {networkInfo.online ? 'YES' : 'NO'}</div>
              </div>
            </div>
            
            <div>
              <div className="font-medium text-gray-700">Mode Detection:</div>
              <div className="text-gray-600 space-y-1">
                <div>Auto Lite: {autoLiteEnabled ? 'YES' : 'NO'}</div>
                <div>User Override: {isUserOverride ? 'YES' : 'NO'}</div>
                <div>Stored Mode: {storedMode || 'None'}</div>
              </div>
            </div>
            
            <div className="pt-2 border-t text-gray-500">
              <div>Lite Mode auto-enables for:</div>
              <div>• Data Saver ON</div>
              <div>• 2G/3G connections</div>
              <div>• Slow networks</div>
            </div>
          </CardContent>
        </Card>
      )}
    </div>
  );
};